/**
 * @file Color conversions between hex, RGB, HSL and OKLCH, plus WCAG relative
 * luminance and dominant-color extraction from an image.
 *
 * Color spaces used here:
 * - **sRGB**: the gamma-encoded RGB that hex strings and CSS colors use.
 * - **Linear sRGB**: sRGB with the gamma curve removed, so channel values are
 *   proportional to light intensity. Luminance and OKLab math require it.
 * - **OKLab / OKLCH**: a perceptual color space where equal numeric steps look
 *   like equal visual steps. OKLCH is OKLab in polar form: lightness, chroma
 *   (colorfulness) and hue angle.
 *
 * Conversions between spaces are 3x3 matrix multiplications on RGB/LMS
 * vectors, with non-linear steps (gamma curve, cube root) in between.
 */

const HEX_COLOR = /^#([0-9a-f]{3}|[0-9a-f]{6})$/i

/**
 * Checks for a `#rgb` or `#rrggbb` string (case-insensitive).
 */
export const isHexColor = (color: string | null | undefined): color is string =>
  typeof color === "string" && HEX_COLOR.test(color)

/**
 * Parses a hex color into sRGB channels.
 *
 * @param hexColor - `#rgb` or `#rrggbb`; the leading `#` is optional.
 * @returns Channels in the 0-255 range.
 */
export const hexToRgb = (hexColor: string) => {
  hexColor = hexColor.replace("#", "")

  // Convert 3-digit hex to 6-digit hex
  if (hexColor.length === 3) {
    hexColor = hexColor
      .split("")
      .map((char) => char + char)
      .join("")
  }

  const r = parseInt(hexColor.substring(0, 2), 16)
  const g = parseInt(hexColor.substring(2, 4), 16)
  const b = parseInt(hexColor.substring(4, 6), 16)

  return { r, g, b }
}

/**
 * Converts sRGB to HSL.
 *
 * @param r - Red, 0-1.
 * @param g - Green, 0-1.
 * @param b - Blue, 0-1.
 * @returns Hue, saturation and lightness, each 0-1 (hue 1 = 360°).
 * @see https://www.niwa.nu/2013/05/math-behind-colorspace-conversions-rgb-hsl/
 */
export const rgbToHsl = (r: number, g: number, b: number) => {
  // https://en.wikipedia.org/wiki/Channel_(digital_image)#RGB_images
  const maxRgbChannel = Math.max(r, g, b)
  const minRgbChannel = Math.min(r, g, b)

  let h = 0
  let s = 0
  const l = (maxRgbChannel + minRgbChannel) / 2

  if (maxRgbChannel === minRgbChannel) {
    h = s = 0
  } else {
    const channelRange = maxRgbChannel - minRgbChannel
    s =
      l > 0.5
        ? channelRange / (2 - maxRgbChannel - minRgbChannel)
        : channelRange / (maxRgbChannel + minRgbChannel)
    if (maxRgbChannel === r) {
      h = (g - b) / channelRange + (g < b ? 6 : 0)
    } else if (maxRgbChannel === g) {
      h = (b - r) / channelRange + 2
    } else {
      h = (r - g) / channelRange + 4
    }

    h = h / 6
  }

  return { h, s, l }
}

/**
 * Converts HSL to a hex color.
 *
 * @param h - Hue, 0-1 (1 = 360°).
 * @param s - Saturation, 0-1.
 * @param l - Lightness, 0-1.
 * @returns A `#rrggbb` string.
 */
export const hslToHex = (h: number, s: number, l: number) => {
  const hueToChannelValue = (
    channelMin: number,
    channelMax: number,
    huePosition: number
  ) => {
    if (huePosition < 0) huePosition += 1
    if (huePosition > 1) huePosition -= 1
    if (huePosition < 1 / 6)
      return channelMin + (channelMax - channelMin) * 6 * huePosition
    if (huePosition < 1 / 2) return channelMax
    if (huePosition < 2 / 3)
      return channelMin + (channelMax - channelMin) * (2 / 3 - huePosition) * 6
    return channelMin
  }

  const channelMax = l < 0.5 ? l * (1 + s) : l + s - l * s
  const channelMin = 2 * l - channelMax
  const rgb = [
    hueToChannelValue(channelMin, channelMax, h + 1 / 3),
    hueToChannelValue(channelMin, channelMax, h),
    hueToChannelValue(channelMin, channelMax, h - 1 / 3),
  ]

  const finalHex = rgb
    .map((x) =>
      Math.round(x * 255)
        .toString(16)
        .padStart(2, "0")
    )
    .join("")

  return `#${finalHex}`
}

type Vector3 = [number, number, number]
type Matrix3 = [Vector3, Vector3, Vector3]

/** Dot product of two 3-component vectors. */
const dot = ([a, b, c]: Vector3, [x, y, z]: Vector3) => a * x + b * y + c * z

/** Multiplies a 3x3 matrix by a column vector. */
const multiply = ([row1, row2, row3]: Matrix3, vector: Vector3): Vector3 => [
  dot(row1, vector),
  dot(row2, vector),
  dot(row3, vector),
]

/**
 * Constants of the sRGB transfer function (gamma curve), IEC 61966-2-1.
 * Below the threshold the curve is a straight line; above it, a power curve.
 */
const SRGB_TRANSFER = {
  encodedThreshold: 0.04045,
  linearThreshold: 0.0031308,
  linearSlope: 12.92,
  offset: 0.055,
  exponent: 2.4,
}

/**
 * Rec. 709 weights of linear R, G and B in perceived brightness, as used by
 * WCAG 2.x relative luminance.
 */
const REC_709_LUMINANCE_WEIGHTS: Vector3 = [0.2126, 0.7152, 0.0722]

/*
 * Björn Ottosson's OKLab matrices (M1, M2 and their inverses). LMS models the
 * response of the eye's long, medium and short wavelength cones.
 *
 * Forward:  linear sRGB -> LMS -> cube root -> OKLab
 * Inverse:  OKLab -> LMS cube root -> cube -> linear sRGB
 *
 * https://bottosson.github.io/posts/oklab/#converting-from-linear-srgb-to-oklab
 */
const LINEAR_SRGB_TO_LMS: Matrix3 = [
  [0.4122214708, 0.5363325363, 0.0514459929],
  [0.2119034982, 0.6806995451, 0.1073969566],
  [0.0883024619, 0.2817188376, 0.6299787005],
]
const LMS_CUBE_ROOT_TO_OKLAB: Matrix3 = [
  [0.2104542553, 0.793617785, -0.0040720468],
  [1.9779984951, -2.428592205, 0.4505937099],
  [0.0259040371, 0.7827717662, -0.808675766],
]
const OKLAB_TO_LMS_CUBE_ROOT: Matrix3 = [
  [1, 0.3963377774, 0.2158037573],
  [1, -0.1055613458, -0.0638541728],
  [1, -0.0894841775, -1.291485548],
]
const LMS_TO_LINEAR_SRGB: Matrix3 = [
  [4.0767416621, -3.3077115913, 0.2309699292],
  [-1.2684380046, 2.6097574011, -0.3413193965],
  [-0.0041960863, -0.7034186147, 1.707614701],
]

/**
 * Removes sRGB gamma from one channel (0-1), giving linear light (0-1).
 */
const srgbToLinear = (channel: number) => {
  const { encodedThreshold, linearSlope, offset, exponent } = SRGB_TRANSFER

  return channel <= encodedThreshold
    ? channel / linearSlope
    : ((channel + offset) / (1 + offset)) ** exponent
}

/**
 * Applies sRGB gamma to one linear channel (0-1), the inverse of
 * {@link srgbToLinear}. Does not clamp.
 */
const linearToSrgb = (channel: number) => {
  const { linearThreshold, linearSlope, offset, exponent } = SRGB_TRANSFER

  return channel <= linearThreshold
    ? channel * linearSlope
    : (1 + offset) * channel ** (1 / exponent) - offset
}

/**
 * Converts a hex color to OKLCH.
 *
 * @param hexColor - `#rgb` or `#rrggbb`.
 * @returns `l` lightness (0-1), `c` chroma (0 to ~0.37 within sRGB) and
 * `h` hue in radians (-π to π).
 */
export const hexToOklch = (hexColor: string) => {
  const { r, g, b } = hexToRgb(hexColor)
  const linearRgb: Vector3 = [
    srgbToLinear(r / 255),
    srgbToLinear(g / 255),
    srgbToLinear(b / 255),
  ]

  const [long, medium, short] = multiply(LINEAR_SRGB_TO_LMS, linearRgb)
  const [l, a, bAxis] = multiply(LMS_CUBE_ROOT_TO_OKLAB, [
    Math.cbrt(long),
    Math.cbrt(medium),
    Math.cbrt(short),
  ])

  return { l, c: Math.hypot(a, bAxis), h: Math.atan2(bAxis, a) }
}

/**
 * Converts OKLCH to linear sRGB. Channels fall outside 0-1 when the color is
 * outside the sRGB gamut.
 */
const oklchToLinearRgb = (l: number, c: number, h: number) => {
  const [long, medium, short] = multiply(OKLAB_TO_LMS_CUBE_ROOT, [
    l,
    c * Math.cos(h),
    c * Math.sin(h),
  ])

  return multiply(LMS_TO_LINEAR_SRGB, [long ** 3, medium ** 3, short ** 3])
}

// Absorbs floating-point error from the matrix round trip
const GAMUT_TOLERANCE = 1e-6
// A gamut-mapped color sits at most this much chroma inside the sRGB edge
const CHROMA_STEP = 0.002

const isInSrgbGamut = (linearRgb: Vector3) =>
  linearRgb.every(
    (channel) => channel >= -GAMUT_TOLERANCE && channel <= 1 + GAMUT_TOLERANCE
  )

/**
 * Converts OKLCH to a hex color. Colors outside the sRGB gamut lose chroma in
 * {@link CHROMA_STEP} decrements until they fit; lightness and hue are kept.
 *
 * @param l - Lightness, 0-1.
 * @param c - Chroma, 0 or more.
 * @param h - Hue in radians.
 * @returns A `#rrggbb` string.
 */
export const oklchToHex = (l: number, c: number, h: number) => {
  let chroma = c
  let linearRgb = oklchToLinearRgb(l, chroma, h)

  while (!isInSrgbGamut(linearRgb) && chroma > 0) {
    chroma = Math.max(chroma - CHROMA_STEP, 0)
    linearRgb = oklchToLinearRgb(l, chroma, h)
  }

  const finalHex = linearRgb
    .map((channel) =>
      Math.round(Math.min(Math.max(linearToSrgb(channel), 0), 1) * 255)
        .toString(16)
        .padStart(2, "0")
    )
    .join("")

  return `#${finalHex}`
}

/**
 * WCAG 2.x relative luminance of a hex color: 0 for black, 1 for white.
 * Feeds the WCAG contrast ratio `(L1 + 0.05) / (L2 + 0.05)`.
 *
 * @see https://www.w3.org/TR/WCAG21/#dfn-relative-luminance
 */
export const getLuminance = (hex: string) => {
  const { r, g, b } = hexToRgb(hex)
  const linearRgb: Vector3 = [
    srgbToLinear(r / 255),
    srgbToLinear(g / 255),
    srgbToLinear(b / 255),
  ]

  return dot(REC_709_LUMINANCE_WEIGHTS, linearRgb)
}

/**
 * This method gets an URL of an image and tries to extract a dominant color.
 * The easiest way to achieve it without going for third party libraries is
 * to draw the image on a canvas and then analyse it pixel by pixel.
 * This operation is not particularly expensive, so it doesn't need to be
 * memoized.
 *
 * It's possible that if the image is very colorful the resulting dominant
 * color will be skewed towards grays or browns, but this is for the designers
 * to figure out.
 *
 * Gray and partially transparent pixels are ignored. Channels are bucketed
 * into steps of 16, so the result is the most common bucket, not an exact
 * pixel color.
 *
 * @param imageUrl - Must be served with CORS headers; the image is requested
 * with `crossOrigin = "anonymous"`, so without them it fails to load.
 * @returns A `#rrggbb` string. Rejects when there is no URL, the image fails
 * to load, the canvas has no 2D context, or no opaque non-gray pixels are
 * found.
 */
export const getDominantColorFromImage = (
  imageUrl?: string | null
): Promise<string> => {
  const MAX_THUMBNAIL_SIZE = 50
  // Near-identical shades share a bucket and count as one color
  const CHANNEL_BUCKET_SIZE = 16

  const quantizeChannel = (channel: number) =>
    Math.floor(channel / CHANNEL_BUCKET_SIZE) * CHANNEL_BUCKET_SIZE

  return new Promise((resolve, reject) => {
    if (!imageUrl) {
      reject(new Error("No image to derive color from"))
      return
    }

    const img = new Image()
    img.crossOrigin = "anonymous"
    img.src = imageUrl

    img.onerror = reject

    img.onload = () => {
      // Images to analyze can end up being very big, so we downscale
      // them to a small thumbnail size to make the analysis less expensive.
      // We keep aspect ratio of the original image.
      const { naturalWidth, naturalHeight } = img
      const scale = MAX_THUMBNAIL_SIZE / Math.max(naturalWidth, naturalHeight)
      const thumbnailWidth = Math.round(naturalWidth * scale)
      const thumbnailHeight = Math.round(naturalHeight * scale)

      const canvas = document.createElement("canvas")
      canvas.width = thumbnailWidth
      canvas.height = thumbnailHeight
      const ctx = canvas.getContext("2d")

      if (!ctx) {
        reject(new Error("Canvas 2D context is unavailable"))
        return
      }

      ctx.drawImage(img, 0, 0, thumbnailWidth, thumbnailHeight)

      const { data: pixels } = ctx.getImageData(
        0,
        0,
        thumbnailWidth,
        thumbnailHeight
      )
      const counts: Record<string, number> = {}

      for (let offset = 0; offset < pixels.length; offset += 4) {
        const red = pixels[offset]
        const green = pixels[offset + 1]
        const blue = pixels[offset + 2]
        const alpha = pixels[offset + 3]

        const isPixelGray = red === green && red === blue
        const isPixelTransparent = alpha === undefined || alpha < 255
        const isAnyChannelUndefined =
          red === undefined || green === undefined || blue === undefined

        const shouldSkipPixel =
          isPixelGray || isPixelTransparent || isAnyChannelUndefined

        if (shouldSkipPixel) continue

        const quantizedRed = quantizeChannel(red)
        const quantizedGreen = quantizeChannel(green)
        const quantizedBlue = quantizeChannel(blue)
        const hex = `#${quantizedRed
          .toString(16)
          .padStart(2, "0")}${quantizedGreen
          .toString(16)
          .padStart(2, "0")}${quantizedBlue.toString(16).padStart(2, "0")}`
        counts[hex] = (counts[hex] ?? 0) + 1
      }

      const dominant = Object.entries(counts).sort(
        (a, b) => b[1] - a[1]
      )[0]?.[0]

      if (dominant) {
        resolve(dominant)
      } else {
        reject(new Error("No opaque pixels found"))
      }
    }
  })
}
